import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import ReactSelect, { components } from 'react-select';
import classes from './Dropdown.module.css';
import clsx from 'clsx';

export const Dropdown = ({
  isOptional = true,
  options = [],
  label,
  customStyle,
  disabled = false,
  value,
  setter,
  noBorder,
  placeholder = 'Select',
  placeholderStyle,
  isMulti = false,
  style,
  showField,
  Components = {},
  labelClassName,
  indicatorColor = 'var(--icon-color)',
  optionLabel,
  optionValue,
  leftIconClass,
  selectRef,
  isSearchable = true,
  dropdownIndicator = true,
  dropdownContainer,
  leftIcon,
  onBlur = () => {},
  className,
  error = false,
  errorText = '',
  optionsWidth = '100%',
  transparentBg = false,
  parentClass,
  menuPortalTarget = document.body,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputContainerStyles = {
    ...(leftIcon && { paddingLeft: '50px' })
  };

  const errorStyles = {
    ...(errorText && { borderColor: 'var(--error-color)', backgroundColor: 'var(--light-red)' })
  };

  const DropdownIndicator = ({ selectProps, ...props }) => {
    const { menuIsOpen } = selectProps;
    setIsOpen(menuIsOpen);
    return !showField ? (
      <components.DropdownIndicator {...props}>
        <IoIosArrowDown
          size={20}
          color={indicatorColor}
          style={{
            transition: 'transform 0.3s ease',
            transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </components.DropdownIndicator>
    ) : null;
  };

  const dropDownStyle = {
    control: (styles, { isDisabled }) => ({
      ...styles,
      backgroundColor: isDisabled
        ? 'var(--place-holder)'
        : transparentBg
        ? '#fff'
        : 'var(--secondary-color)',
      cursor: disabled ? 'not-allowed !important' : 'pointer',
      borderColor: transparentBg ? '#fff' : 'var(--secondary-color)',
      padding: '4px 0px 4px 6px',
      boxShadow: 'none',
      borderRadius: 'var(--field-border-radius)',
      fontFamily: 'NotoSans-regular',
      fontSize: transparentBg ? 24 : 14,
      letterSpacing: '1.4px',
      minWidth: '37px',
      ...errorStyles,
      ':hover': {
        borderColor: transparentBg ? '#fff' : 'var(--secondary-color)',
        ...errorStyles,
        cursor: isDisabled ? 'not-allowed !important' : 'pointer'
      },
      ':focus': {
        borderColor: 'var(--primary-color)',
        backgroundColor: 'var(--light-blue)'
      },
      ':active': {
        ...errorStyles
      },
      ...customStyle
    })
    // Other styles remain the same
  };
  useEffect(() => {
    // Add or remove a class to the body when dropdown is open
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    // Cleanup function to ensure the class is removed on unmount
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);
  return (
    <div className={classes.Container}>
      <style jsx>{`
        .dropdownContainer {
          // position: absolute;
          position: relative;
          z-index: 9999;
        }

        .DropdownOptionContainer__menu {
          width: ${optionsWidth} !important;
          box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.25);
        }
        input::placeholder {
          font-size: 16px;
          letter-spacing: -0.25px;
        }
        .DropdownOptionContainer__single-value {
          color: var(--text-color);
          fontsize: ${transparentBg ? '24px' : 'inherit'};
          font-family: ${transparentBg ? 'NotoSans-bold' : 'NotoSans-regular'};
          margin-inline: ${transparentBg ? '0px' : '2px'};
        }
        .DropdownOptionControl {
          border: 1px solid var(--secondary-color);
          background-color: var(--light-blue) !important;
          background: var(--light-blue) !important;
          border-radius: 8px !important;
        }
        .css-1tawu86-control {
          background-color: var(--light-blue);
          border-radius: var(--field-border-radius) !important;
        }

        .DropdownOptionContainer__control:focus-within {
          border-color: var(--primary-color) !important;
          background-color: var(--light-blue) !important;
        }
        .DropdownOptionContainer__control:focus {
          border-color: var(--primary-color) !important;
          background-color: var(--light-blue) !important;
        }
        .DropdownOptionContainer__control {
          padding: ${transparentBg ? '0px' : '4px 0px 4px 6px'};
          border-radius: 4px !important;
        }
        .DropdownOptionContainer__control > div {
          padding: ${transparentBg ? '0px' : '2px 8px'};
        }
        .error .DropdownOptionContainer__control:focus-within {
          border-color: var(--error-color) !important;
          background-color: var(--light-red) !important;
        }
        .error .DropdownOptionContainer__control:focus {
          border-color: var(--error-color) !important;
          background-color: var(--light-red) !important;
        }
        div.error > .css-1tawu86-control:hover {
          border-color: var(--error-color) !important;
          background-color: var(--light-red) !important;
        }
        .${classes.dropdownWrapper} {
          background-color: var(--secondary-color) !important;
        }
        div.transparentBg {
          background-color: #ffff !important;
        }
        div.transparentBg > .css-1tawu86-control:hover {
          border-color: #fff !important;
          background-color: #fff !important;
        }
        div.transparentBg .DropdownOptionContainer__control:focus-within {
          border-color: #fff !important;
          background-color: #fff !important;
        }
        div.transparentBg .DropdownOptionContainer__control:focus {
          border-color: #ffff !important;
          background-color: #fff !important;
        }
        .error label {
          color: var(--error-color);
        }
        .DropdownOptionContainer__single-value--is-disabled {
          color: var(--common-disable) !important;
        }
        .DropdownOptionContainer__placeholder {
          font-size: 16px !important;
          letter-spacing: -0.25px !important;
        }
      `}</style>

      <div className={clsx({ error: errorText, transparentBg: transparentBg })}>
        {label && (
          <label
            htmlFor={`dropdown${label}`}
            className={`${classes.label} ${labelClassName || ''} ${
              disabled ? classes.disabled : ''
            }`}>
            {label}
            {isOptional && <span className={`steric`}> *</span>}
          </label>
        )}

        <div
          className={clsx(classes.dropdownContainer, {
            leftIconClass: leftIconClass,
            [classes.disabledWrapper]: disabled
          })}
          style={inputContainerStyles}>
          {leftIcon && <div className={classes.leftIconBox}>{leftIcon}</div>}

          <div
            className={clsx(classes.dropdownWrapper, parentClass)}
            style={{ ...(showField && { width: 0 }), ...dropdownContainer }}>
            <ReactSelect
              onBlur={onBlur}
              inputId={`dropdown${label}`}
              value={value}
              onChange={setter}
              className={`${classes.reactSelect} ${className || ''}`}
              isMulti={isMulti}
              isDisabled={disabled}
              placeholder={placeholder}
              options={options}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 99999999999999 }), // Ensure dropdown is on top
                ...dropDownStyle,
                ...style
              }}
              onMenuOpen={() => setIsOpen(true)} // Open dropdown
              onMenuClose={() => setIsOpen(false)} // Close dropdown
              isClearable={false}
              isSearchable={isSearchable}
              classNamePrefix="DropdownOptionContainer"
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: dropdownIndicator ? DropdownIndicator : null,
                ...Components
              }}
              getOptionLabel={(option) => (optionLabel ? option[optionLabel] : option.label)}
              getOptionValue={(option) => (optionValue ? option[optionValue] : option.value)}
              ref={selectRef}
              menuPortalTarget={menuPortalTarget} // Add this prop
              {...props}
            />
          </div>
        </div>

        {errorText && <p className={`errorText`}>{errorText}</p>}
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  options: PropTypes.array.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.object.isRequired,
  setter: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  customStyle: PropTypes.object,
  style: PropTypes.object,
  Components: PropTypes.object,
  labelClassName: PropTypes.string,
  indicatorColor: PropTypes.string,
  optionLabel: PropTypes.string,
  optionValue: PropTypes.string,
  leftIconClass: PropTypes.string,
  selectRef: PropTypes.object,
  isSearchable: PropTypes.bool,
  dropdownIndicator: PropTypes.bool,
  dropdownContainer: PropTypes.object,
  leftIcon: PropTypes.node,
  className: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  optionsWidth: PropTypes.string
};

export default Dropdown;
